/** @license num-to-korean v0.4.3
* https://github.com/huskyhoochu/num-to-korean
* (c) 2020-2021 Seung Hyung Soo
* This source code is licensed under the MIT license found in the
* LICENSE file in the root directory of this source tree.
*/

'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

function splitEvery(n, arr) {
    var result = [];
    var index = 0;
    while (index < arr.length) {
        result.push(arr.slice(index, (index += n)));
    }
    return result;
}
function dropLastWhile(pred, arr) {
    var index = arr.length - 1;
    while (index >= 0 && pred(arr[index])) {
        index -= 1;
    }
    return arr.slice(0, index + 1);
}

// 한글로 바꿀 숫자 배열
var textSymbol = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
// 4자리마다 반복되는 자릿수 배열
var powerSymbol = ['', '십', '백', '천'];
// 4자리마다 커지는 단위수 배열
var dotSymbol = ['', '만', '억', '조', '경'];
// eslint-disable-next-line no-restricted-globals
var isInteger = function (num) { return isFinite(num) && Math.floor(num) === num; };
var validate = function (num) { return (isInteger(num) ? num : 0); };
var getAtomic = function (num) {
    return num
        .toString()
        .split('')
        .map(function (numText) { return parseInt(numText, 10); })
        .reverse()
        .map(function (item, index) {
        var powerIndex = index % 4;
        var dotIndex = Math.ceil(index / 4);
        var power = item === 0 ? '' : powerSymbol[powerIndex];
        var dot = powerIndex === 0 ? dotSymbol[dotIndex] : '';
        return [item, power, dot];
    });
};
var reduceAtomic = function (atomic) {
    var reduce = atomic.map(function (item) {
        var newItem = __spreadArrays(item);
        newItem[0] = textSymbol[newItem[0]] || '';
        return newItem.join('');
    });
    return splitEvery(4, reduce)
        .map(function (item) { return (dotSymbol.indexOf(item.join('')) > 0 ? ['', '', '', ''] : item); })
        .reduce(function (acc, val) { return acc.concat(val); }, [])
        .reverse();
};
var addSpacing = function (reduced) {
    return reduced.filter(function (token) { return token; })
        .map(function (token) {
        if (dotSymbol.indexOf(token.slice(-1)) > 0) {
            return token + " ";
        }
        return token;
    })
        .join('')
        .trim();
};
var removeOneStr = function (list) {
    if (list.length === 1) {
        return list;
    }
    var filtered = [];
    for (var i = 0; i < list.length; i += 1) {
        // 일의자리의 "일"은 생략되어선 안 된다
        if (i === list.length - 1) {
            filtered.push(list[i]);
            break;
        }
        // 십만자리 이상일 때 만, 억, 조 단위의 "일"은 생략되어선 안 된다
        // 다만 만의자리일 때 만의자리의 "일"은 생략되어야 한다
        if (list.length > 5 && dotSymbol.indexOf(list[i].slice(1)) > 0) {
            filtered.push(list[i]);
            // eslint-disable-next-line no-continue
            continue;
        }
        // 그 외의 "일"은 생략된다
        if (list[i].slice(0, 1) === textSymbol[1]) {
            filtered.push(list[i].slice(1));
        }
        else {
            filtered.push(list[i]);
        }
    }
    return filtered;
};
var getNormal = function (num) {
    return reduceAtomic(getAtomic(validate(num))).join('');
};
var getLingual = function (num) {
    return removeOneStr(reduceAtomic(getAtomic(validate(num)))).join('');
};
var getSpacing = function (num) {
    return addSpacing(reduceAtomic(getAtomic(validate(num))));
};
var getLingualSpacing = function (num) {
    return addSpacing(removeOneStr(reduceAtomic(getAtomic(validate(num)))));
};
var getMixed = function (num) {
    var mixedAtomic = getAtomic(validate(num)).map(function (item) { return "" + item[0] + item[2]; });
    var reduced = splitEvery(4, mixedAtomic)
        .map(function (item) {
        var droppedZero = dropLastWhile(function (x) { return parseInt(x, 10) === 0; }, item);
        if (droppedZero.length === 4) {
            droppedZero.splice(3, 0, ',');
        }
        return droppedZero;
    })
        .reduce(function (acc, val) { return acc.concat(val); }, [])
        .reverse();
    var result = addSpacing(reduced);
    return result === '' ? '0' : result;
};
var FormatOptions = {
    SPACING: 'spacing',
    MIXED: 'mixed',
    LINGUAL: 'lingual',
    LINGUAL_SPACING: 'lingual_spacing',
};
/**
 * Converts a number to Korean notation.
 * @param num A number to convert into Korean notation.
 * @param formatOptions A string to select a format.
 */
function numToKorean(num, formatOptions) {
    if (formatOptions === FormatOptions.SPACING) {
        return getSpacing(num);
    }
    if (formatOptions === FormatOptions.MIXED) {
        return getMixed(num);
    }
    if (formatOptions === FormatOptions.LINGUAL) {
        return getLingual(num);
    }
    if (formatOptions === FormatOptions.LINGUAL_SPACING) {
        return getLingualSpacing(num);
    }
    return getNormal(num);
}

exports.FormatOptions = FormatOptions;
exports.numToKorean = numToKorean;
